// Import types for factory functions
import type { RAGEnv, RAGConfig, IngestionRequest, QueryResponse } from './types/index.js';
import { RAGManager } from './pipeline/rag-manager.js';
import { RAGPipeline } from './pipeline/rag-pipeline.js';

// Export main classes
export { RAGPipeline } from './pipeline/rag-pipeline.js';
export { RAGManager } from './pipeline/rag-manager.js';

// Export processing components
export { DocumentLoader } from './processing/document-loader.js';
export { DocumentChunker } from './processing/chunking.js';
export { EmbeddingService } from './processing/embedding-service.js';

// Export retrieval components
export { HybridRetriever } from './retrieval/retriever.js';

// Export generation components
export { AnswerGenerator } from './generation/answer-generator.js';

// Export all types
export type {
  // Core types
  Document,
  DocumentMetadata,
  DocumentFormat,
  DocumentSource,
  Chunk,
  ChunkMetadata,
  ChunkingConfig,
  ChunkingStrategy,
  
  // Configuration types
  RAGConfig,
  EmbeddingConfig,
  
  // Request/Response types
  IngestionRequest,
  QueryRequest,
  QueryResponse,
  QueryStreamChunk,
  ContextResult,
  
  // Pipeline types
  RAGPipelineInfo,
  RAGPipelineStatus,
  ProcessingResult,
  BatchProcessingResult,
  
  // Analytics types
  RAGAnalyticsEvent,
  
  // Environment types
  RAGEnv,
  
  // Error types
  RAGError,
  DocumentProcessingError,
  EmbeddingError,
  RetrievalError,
  GenerationError,
} from './types/index.js';

// Export schemas for validation
export {
  DocumentSchema,
  ChunkSchema,
  RAGConfigSchema,
  IngestionRequestSchema,
  QueryRequestSchema,
} from './types/index.js';

// Re-export key AutoRAG utilities
export { createAutoDocumentProcessor } from './processing/auto-document-processor.js';
export { createContinuousIndexer } from './indexing/continuous-indexer.js';
export { createEnterpriseSecurityManager } from './security/enterprise-security.js';
export { createVectorizeStorage } from './storage/vectorize-storage.js';

// Export utilities
export {
  generateId,
  sanitizeText,
  extractHeaders,
  estimateTokens,
  createOverlappingChunks,
  findSentenceBoundaries,
  findParagraphBoundaries,
  cosineSimilarity,
  textSimilarity,
  extractKeywords,
  normalizeQuery,
  formatDuration,
  formatFileSize,
  hashContent,
} from './core/utils.js';

// Factory functions
export function createRAGManager(env: RAGEnv): RAGManager {
  return new RAGManager(env);
}

export function createRAGPipeline(env: RAGEnv, config: RAGConfig): RAGPipeline {
  return new RAGPipeline(env, config);
}

// Default configurations
export const DEFAULT_RAG_CONFIG: Partial<RAGConfig> = {
  embedding: {
    model: 'text-embedding-ada-002',
    provider: 'openai',
    dimensions: 1536,
    batchSize: 100,
    maxRetries: 3,
    timeout: 30000,
  },
  chunking: {
    strategy: 'recursive',
    size: 512,
    overlap: 128,
    minChunkSize: 50,
    maxChunkSize: 2048,
    preserveStructure: false,
    separators: ['\n\n', '\n', '. ', ' ', ''],
  },
  retrieval: {
    topK: 5,
    minSimilarity: 0.7,
    hybridWeight: 0.7,
    maxContextTokens: 4000,
  },
  generation: {
    model: 'gpt-3.5-turbo',
    provider: 'openai',
    temperature: 0.1,
    maxTokens: 1000,
    includeSource: true,
    streaming: false,
  },
  analytics: {
    enabled: true,
    trackQueries: true,
    trackPerformance: true,
    sampleRate: 1.0,
  },
};

// Convenience functions for common operations
export async function quickRAG(
  env: RAGEnv,
  options: {
    name: string;
    documents: IngestionRequest[];
    query: string;
  }
): Promise<QueryResponse> {
  const manager = createRAGManager(env);
  await manager.initialize();
  
  const pipeline = await manager.createDefaultPipeline(options.name);
  
  // Ingest documents
  await pipeline.ingest(options.documents);
  
  // Query
  return pipeline.query({
    query: options.query,
    topK: 5,
    minSimilarity: 0.7,
    includeSource: true,
    streaming: false,
    rerankResults: true,
  });
}

// Version information
export const VERSION = '0.1.0';